import { Clock, ArrowUpDown, DollarSign, Cpu, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface TelemetryData {
  model?: string;
  latency_ms?: number;
  tokens_in?: number;
  tokens_out?: number;
  est_cost_usd?: number;
  run_id?: string;
  created_at?: string;
}

interface TelemetryPanelProps {
  telemetry?: TelemetryData;
}

export function TelemetryPanel({ telemetry }: TelemetryPanelProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  if (!telemetry) {
    return null;
  }

  const badges = [
    { 
      icon: Clock, 
      label: "Latency", 
      value: telemetry.latency_ms ? `${(telemetry.latency_ms / 1000).toFixed(1)}s` : "—" 
    },
    { 
      icon: ArrowUpDown, 
      label: "In", 
      value: telemetry.tokens_in ? `${(telemetry.tokens_in / 1000).toFixed(1)}k` : "—" 
    },
    { 
      icon: ArrowUpDown, 
      label: "Out", 
      value: telemetry.tokens_out ? `${(telemetry.tokens_out / 1000).toFixed(1)}k` : "—" 
    },
    { 
      icon: DollarSign, 
      label: "Cost", 
      value: telemetry.est_cost_usd ? `$${telemetry.est_cost_usd.toFixed(3)}` : "—" 
    },
    { 
      icon: Cpu, 
      label: "Model", 
      value: telemetry.model || "Unknown" 
    }
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {badges.map((badge, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
          >
            <badge.icon className="h-3 w-3" />
            <span className="hidden sm:inline text-muted-foreground">{badge.label}</span>
            <span className="font-medium">{badge.value}</span>
          </Badge>
        ))}
      </div>

      <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
          >
            {isDetailsOpen ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Hide Details
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Show Details
              </>
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 p-3 bg-muted/50 rounded-md">
            <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
              {JSON.stringify(telemetry, null, 2)}
            </pre>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}